import React, { useState, useEffect, useCallback, useMemo } from "react";
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar, PieChart, Pie, Cell } from "recharts";
import { MapContainer, TileLayer, CircleMarker, Tooltip as MapTip } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import {
    BarChart2, Users, Eye, Clock, MousePointerClick, RefreshCw, Wifi, TrendingUp, TrendingDown, Globe2, Smartphone, Monitor, Tablet,
    AlertTriangle, Bot, Link2, FileText, Activity, CalendarClock,
} from "lucide-react";
import api from "../../utils/api";

// Traffic Analytics (super-admin + admin): real visitors to tinyglobalop.com — bots counted
// separately — vs the previous period, time on site from session heartbeats, busiest days
// and hours (Sydney time), where visitors are, what they use, where they came from.
// Data: GET /api/analytics/admin/traffic?days= (analyticsController.getTrafficAdmin).

const RANGES = [[7, "7d"], [30, "30d"], [90, "90d"], [365, "12m"]];
const PURPLE = "#6366f1", GREEN = "#10b981";
const PALETTE = ["#6366f1", "#10b981", "#f59e0b", "#06b6d4", "#ef4444", "#8b5cf6", "#94a3b8"];
const DEVICE_ICON = { desktop: Monitor, mobile: Smartphone, tablet: Tablet };
const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const card = "bg-white rounded-2xl border border-slate-200 shadow-sm";

const num = n => (n == null ? "—" : Number(n).toLocaleString("en-AU"));
const dur = s => (s == null ? "—" : s < 60 ? `${s}s` : `${Math.floor(s / 60)}m ${String(s % 60).padStart(2, "0")}s`);
const flag = code => (code && code.length === 2 && /^[A-Z]{2}$/i.test(code) ? `https://flagcdn.com/24x18/${code.toLowerCase()}.png` : null);
const countryName = code => { try { return new Intl.DisplayNames(["en"], { type: "region" }).of(code); } catch { return code; } };
const ago = ms => { const s = Math.floor((Date.now() - ms) / 1000); return s < 60 ? `${s}s ago` : s < 3600 ? `${Math.floor(s / 60)}m ago` : s < 86400 ? `${Math.floor(s / 3600)}h ago` : `${Math.floor(s / 86400)}d ago`; };
const dayLabel = (iso, days) => { const d = new Date(`${iso}T00:00:00`); return days <= 7 ? d.toLocaleDateString("en-AU", { weekday: "short", day: "numeric" }) : d.toLocaleDateString("en-AU", { day: "numeric", month: "short" }); };

export default function TrafficAnalytics() {
    const [days, setDays] = useState(30);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [metric, setMetric] = useState("visitors");
    const [geoTab, setGeoTab] = useState("map");

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data: d } = await api.get("/api/analytics/admin/traffic", { params: { days } });
            setData(d);
            setError("");
        } catch (e) {
            setError(e?.response?.data?.error || "Couldn't load traffic data.");
        } finally {
            setLoading(false);
        }
    }, [days]);
    // Loads when the page opens and on Refresh — no timer, so an open page costs nothing
    useEffect(() => { load(); }, [load]);

    const t = data?.totals || {};
    const daily = useMemo(() => (data?.daily || []).map(d => ({ ...d, label: dayLabel(d.date, days) })), [data, days]);
    const stale = data?.tracking?.daysSinceLast != null && data.tracking.daysSinceLast >= 2;

    return (
        <div className="max-w-7xl mx-auto py-8 px-4 space-y-6">
            {/* Header */}
            <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-4">
                <div>
                    <div className="flex items-center gap-3 flex-wrap">
                        <BarChart2 className="text-indigo-500" size={30} />
                        <h1 className="text-3xl font-bold text-navy-900 dark:text-white font-serif">Traffic Analytics</h1>
                        <span className="px-2.5 py-1 rounded-full bg-white border border-slate-200 text-xs font-mono text-slate-500">tinyglobalop.com</span>
                    </div>
                    <p className="text-slate-400 mt-1 text-sm">
                        Real visitors — bots left out — compared with the previous {days === 365 ? "12 months" : `${days} days`}. Times in Sydney.
                        {data?.generatedAt && <> · updated {new Date(data.generatedAt).toLocaleTimeString("en-AU", { hour: "2-digit", minute: "2-digit" })}</>}
                    </p>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                    <span className={`flex items-center gap-1.5 px-3 py-2 rounded-full text-sm font-bold ${data?.liveCount ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>
                        <Wifi size={15} className={data?.liveCount ? "animate-pulse" : ""} /> {num(data?.liveCount || 0)} live now
                    </span>
                    <div className="flex bg-white border border-slate-200 rounded-xl p-1">
                        {RANGES.map(([d, label]) => (
                            <button key={d} onClick={() => setDays(d)} className={`px-3.5 py-1.5 rounded-lg text-sm font-bold ${days === d ? "bg-navy-900 text-white" : "text-slate-500 hover:text-navy-900"}`}>{label}</button>
                        ))}
                    </div>
                    <button onClick={load} className="p-2.5 bg-white border border-slate-200 rounded-xl text-slate-500 hover:text-navy-900" title="Refresh">
                        <RefreshCw size={16} className={loading ? "animate-spin" : ""} />
                    </button>
                </div>
            </div>

            {error && <div className="px-4 py-3 rounded-xl bg-red-50 border border-red-200 text-sm text-red-700">{error}</div>}
            {stale && (
                <div className="px-5 py-4 rounded-2xl bg-red-50 border border-red-200 flex gap-3">
                    <AlertTriangle className="text-red-500 shrink-0 mt-0.5" size={20} />
                    <div className="text-sm text-red-800">
                        <p className="font-bold">No visits recorded for {data.tracking.daysSinceLast} days — last one {new Date(data.tracking.lastPageviewAt).toLocaleDateString("en-AU", { day: "numeric", month: "long", year: "numeric" })}.</p>
                        <p className="mt-0.5">The live site sends each visit to the backend server. If that server is down or suspended, visits aren't counted — check it's running.</p>
                    </div>
                </div>
            )}

            {/* KPIs */}
            <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
                <Kpi icon={Users} tone="indigo" label="Visitors" value={num(t.visitors)} delta={data?.deltas?.visitors} prev={data?.previous?.visitors} />
                <Kpi icon={Eye} tone="violet" label="Page views" value={num(t.pageViews)} delta={data?.deltas?.pageViews} prev={data?.previous?.pageViews} sub={t.pagesPerVisit ? `${t.pagesPerVisit} per visit` : null} />
                <Kpi icon={Clock} tone="emerald" label="Avg. time on site" value={dur(t.avgTimeSecs)} delta={data?.deltas?.avgTimeSecs} sub="from live heartbeats" />
                <Kpi icon={Activity} tone="amber" label="Engaged visits" value={t.engagedRate == null ? "—" : `${t.engagedRate}%`} sub="30s+ or 2+ pages" />
                <Kpi icon={MousePointerClick} tone="rose" label="Bounce rate" value={t.bounceRate == null ? "—" : `${t.bounceRate}%`} sub={t.botsExcluded ? `${num(t.botsExcluded)} bot hits left out` : "left within 10s"} />
            </div>

            {/* Trend */}
            <section className={`${card} p-6`}>
                <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                    <h2 className="font-bold text-navy-900 flex items-center gap-2"><TrendingUp size={18} className="text-indigo-500" /> Daily traffic</h2>
                    <div className="flex bg-slate-100 rounded-lg p-0.5">
                        {[["visitors", "Visitors"], ["pageViews", "Page views"], ["both", "Both"]].map(([k, label]) => (
                            <button key={k} onClick={() => setMetric(k)} className={`px-3 py-1 rounded-md text-xs font-bold ${metric === k ? "bg-white text-navy-900 shadow-sm" : "text-slate-500"}`}>{label}</button>
                        ))}
                    </div>
                </div>
                <div className="h-72">
                    <ResponsiveContainer width="100%" height="100%">
                        <AreaChart data={daily} margin={{ top: 8, right: 8, left: -18, bottom: 0 }}>
                            <defs>
                                <linearGradient id="gV" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor={PURPLE} stopOpacity={0.35} /><stop offset="100%" stopColor={PURPLE} stopOpacity={0} /></linearGradient>
                                <linearGradient id="gP" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor={GREEN} stopOpacity={0.25} /><stop offset="100%" stopColor={GREEN} stopOpacity={0} /></linearGradient>
                            </defs>
                            <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" vertical={false} />
                            <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#94a3b8" }} tickLine={false} axisLine={false} minTickGap={24} />
                            <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "#94a3b8" }} tickLine={false} axisLine={false} />
                            <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #e2e8f0", fontSize: 12 }} labelFormatter={(_, p) => p?.[0] ? new Date(`${p[0].payload.date}T00:00:00`).toLocaleDateString("en-AU", { weekday: "long", day: "numeric", month: "long" }) : ""} />
                            {metric !== "pageViews" && <Area type="monotone" dataKey="visitors" name="Visitors" stroke={PURPLE} strokeWidth={2} fill="url(#gV)" dot={false} />}
                            {metric !== "visitors" && <Area type="monotone" dataKey="pageViews" name="Page views" stroke={GREEN} strokeWidth={2} fill="url(#gP)" dot={false} />}
                        </AreaChart>
                    </ResponsiveContainer>
                </div>
            </section>

            {/* Geography */}
            <section className={`${card} overflow-hidden`}>
                <div className="flex flex-wrap items-center justify-between gap-3 px-6 pt-5 pb-3">
                    <h2 className="font-bold text-navy-900 flex items-center gap-2"><Globe2 size={18} className="text-indigo-500" /> Where visitors are</h2>
                    <div className="flex items-center gap-3">
                        {data?.unlocated > 0 && <span className="text-xs text-slate-400">{num(data.unlocated)} visitor{data.unlocated === 1 ? "" : "s"} without a known location</span>}
                        <div className="flex bg-slate-100 rounded-lg p-0.5">
                            {[["map", "Map"], ["countries", "Countries"]].map(([k, label]) => (
                                <button key={k} onClick={() => setGeoTab(k)} className={`px-3 py-1 rounded-md text-xs font-bold ${geoTab === k ? "bg-white text-navy-900 shadow-sm" : "text-slate-500"}`}>{label}</button>
                            ))}
                        </div>
                    </div>
                </div>
                <div className="grid grid-cols-1 lg:grid-cols-3">
                    <div className={`lg:col-span-2 h-80 ${geoTab === "countries" ? "hidden lg:block" : ""}`}>
                        <VisitorMap places={data?.places || []} />
                    </div>
                    <div className={`border-t lg:border-t-0 lg:border-l border-slate-100 max-h-80 overflow-auto ${geoTab === "map" ? "hidden lg:block" : ""}`}>
                        <CountryList countries={data?.countries || []} total={(data?.countries || []).reduce((a, c) => a + c.visitors, 0)} />
                    </div>
                </div>
            </section>

            <div className="grid grid-cols-1 xl:grid-cols-5 gap-6">
                {/* When */}
                <section className={`${card} p-6 xl:col-span-3`}>
                    <h2 className="font-bold text-navy-900 flex items-center gap-2 mb-1"><CalendarClock size={18} className="text-indigo-500" /> Busiest days and hours</h2>
                    <p className="text-xs text-slate-400 mb-4">Page views by weekday and hour, Sydney time</p>
                    <Heatmap grid={data?.heatmap} />
                </section>
                {/* Devices */}
                <section className={`${card} p-6 xl:col-span-2`}>
                    <h2 className="font-bold text-navy-900 flex items-center gap-2 mb-3"><Monitor size={18} className="text-indigo-500" /> Devices</h2>
                    <Donut items={data?.devices || []} icons={DEVICE_ICON} />
                </section>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <section className={`${card} p-6`}><h2 className="font-bold text-navy-900 mb-3">Browsers</h2><Bars items={data?.browsers || []} /></section>
                <section className={`${card} p-6`}><h2 className="font-bold text-navy-900 mb-3">Operating systems</h2><Bars items={data?.os || []} /></section>
                <section className={`${card} p-6`}>
                    <h2 className="font-bold text-navy-900 flex items-center gap-2 mb-3"><Link2 size={16} className="text-indigo-500" /> How they arrived</h2>
                    <Bars items={data?.sources || []} />
                    {data?.referrers?.length > 0 && (
                        <div className="mt-4 pt-3 border-t border-slate-100">
                            <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-2">Top referring sites</p>
                            {data.referrers.map(r => <div key={r.name} className="flex justify-between text-sm py-0.5"><span className="text-slate-600 truncate">{r.name}</span><b className="text-navy-900">{r.value}</b></div>)}
                        </div>
                    )}
                </section>
            </div>

            <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
                <section className={`${card} overflow-hidden`}>
                    <h2 className="font-bold text-navy-900 flex items-center gap-2 px-6 pt-5 pb-3"><FileText size={16} className="text-indigo-500" /> Top pages</h2>
                    <table className="w-full text-sm">
                        <thead className="text-[10px] font-bold uppercase tracking-wider text-slate-400 bg-slate-50"><tr><th className="px-6 py-2 text-left">Page</th><th className="px-4 py-2 text-right">Views</th><th className="px-6 py-2 text-right">Visitors</th></tr></thead>
                        <tbody className="divide-y divide-slate-50">
                            {(data?.pages || []).map(p => <tr key={p.page}><td className="px-6 py-2 font-mono text-xs text-navy-900">{p.page}</td><td className="px-4 py-2 text-right tabular-nums">{num(p.views)}</td><td className="px-6 py-2 text-right tabular-nums text-slate-500">{num(p.visitors)}</td></tr>)}
                            {!data?.pages?.length && <tr><td colSpan={3} className="px-6 py-8 text-center text-slate-400">No page views in this period</td></tr>}
                        </tbody>
                    </table>
                </section>
                <section className={`${card} overflow-hidden`}>
                    <h2 className="font-bold text-navy-900 flex items-center gap-2 px-6 pt-5 pb-3"><Users size={16} className="text-indigo-500" /> Recent visits</h2>
                    <div className="max-h-80 overflow-auto divide-y divide-slate-50">
                        {(data?.recent || []).map((r, i) => {
                            const DevIcon = DEVICE_ICON[r.device] || Monitor;
                            return (
                                <div key={i} className="flex items-center gap-3 px-6 py-2 text-sm">
                                    {flag(r.countryCode) ? <img src={flag(r.countryCode)} alt={r.countryCode} className="w-5 h-[15px] rounded-sm shrink-0" /> : <span className="w-5 shrink-0" />}
                                    <span className="flex-1 min-w-0 truncate text-navy-900">{r.city || (r.countryCode ? countryName(r.countryCode) : "Unknown location")} <span className="text-slate-400 font-mono text-xs">{r.page}</span></span>
                                    <DevIcon size={14} className="text-slate-400 shrink-0" />
                                    <span className="text-xs text-slate-400 w-16 text-right shrink-0">{r.browser}</span>
                                    <span className="text-xs text-slate-500 w-14 text-right shrink-0">{dur(r.secs)}</span>
                                    <span className="text-xs text-slate-400 w-16 text-right shrink-0">{ago(r.at)}</span>
                                </div>
                            );
                        })}
                        {!data?.recent?.length && <p className="px-6 py-8 text-center text-sm text-slate-400">No visits in this period</p>}
                    </div>
                </section>
            </div>
            {t.botsExcluded > 0 && <p className="text-xs text-slate-400 flex items-center gap-1.5"><Bot size={13} /> {num(t.botsExcluded)} hits from bots and fake browsers were left out of every figure on this page.</p>}
        </div>
    );
}

function Kpi({ icon: Icon, tone, label, value, delta, prev, sub }) {
    const tones = { indigo: "bg-indigo-50 text-indigo-500", violet: "bg-violet-50 text-violet-500", emerald: "bg-emerald-50 text-emerald-500", amber: "bg-amber-50 text-amber-500", rose: "bg-rose-50 text-rose-500" };
    const up = delta > 0, down = delta < 0;
    return (
        <div className={`${card} p-5`}>
            <div className="flex items-center justify-between">
                <span className={`w-10 h-10 rounded-xl grid place-items-center ${tones[tone]}`}><Icon size={18} /></span>
                {delta != null && (
                    <span className={`flex items-center gap-0.5 text-xs font-bold ${up ? "text-emerald-600" : down ? "text-red-500" : "text-slate-400"}`} title={prev != null ? `Previous period: ${num(prev)}` : undefined}>
                        {up ? <TrendingUp size={13} /> : down ? <TrendingDown size={13} /> : null}{up ? "+" : ""}{delta}%
                    </span>
                )}
            </div>
            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mt-3">{label}</p>
            <p className="text-2xl font-bold text-navy-900 font-serif mt-0.5">{value}</p>
            {sub && <p className="text-[11px] text-slate-400 mt-0.5">{sub}</p>}
        </div>
    );
}

function VisitorMap({ places }) {
    const max = Math.max(1, ...places.map(p => p.visitors));
    return (
        <MapContainer center={[0, 140]} zoom={2} minZoom={1} scrollWheelZoom={false} worldCopyJump className="h-full w-full" style={{ background: "#e8eef5" }}>
            <TileLayer url="https://tile.openstreetmap.org/{z}/{x}/{y}.png" attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors' />
            {places.map((p, i) => (
                <CircleMarker key={i} center={[p.lat, p.lng]} radius={6 + 18 * Math.sqrt(p.visitors / max)} pathOptions={{ color: PURPLE, weight: 1.5, fillColor: PURPLE, fillOpacity: 0.35 }}>
                    <MapTip><b>{p.city || countryName(p.countryCode)}</b>{p.city ? `, ${countryName(p.countryCode)}` : ""}<br />{p.visitors} visitor{p.visitors === 1 ? "" : "s"} · {p.pageViews} page view{p.pageViews === 1 ? "" : "s"}</MapTip>
                </CircleMarker>
            ))}
        </MapContainer>
    );
}

function CountryList({ countries, total }) {
    if (!countries.length) return <p className="px-6 py-10 text-center text-sm text-slate-400">No located visitors in this period</p>;
    return (
        <ol className="divide-y divide-slate-50">
            {countries.map((c, i) => (
                <li key={c.countryCode} className="px-5 py-2.5">
                    <div className="flex items-center gap-2.5 text-sm">
                        <span className="w-4 text-xs text-slate-400">{i + 1}</span>
                        {flag(c.countryCode) && <img src={flag(c.countryCode)} alt="" className="w-5 h-[15px] rounded-sm" />}
                        <span className="flex-1 font-semibold text-navy-900 truncate">{countryName(c.countryCode)}</span>
                        <span className="tabular-nums text-navy-900 font-bold">{c.visitors}</span>
                        <span className="w-10 text-right text-xs text-slate-400">{total ? Math.round((c.visitors / total) * 100) : 0}%</span>
                    </div>
                    <div className="ml-[52px] mt-1 h-1.5 rounded-full bg-slate-100"><div className="h-full rounded-full bg-indigo-400" style={{ width: `${total ? (c.visitors / countries[0].visitors) * 100 : 0}%` }} /></div>
                </li>
            ))}
        </ol>
    );
}

function Heatmap({ grid }) {
    if (!grid) return <div className="h-40 rounded-xl bg-slate-50 animate-pulse" />;
    const max = Math.max(1, ...grid.flat());
    return (
        <div className="overflow-x-auto">
            <div className="inline-grid gap-[3px] min-w-full" style={{ gridTemplateColumns: "34px repeat(24, minmax(14px, 1fr))" }}>
                <span />
                {Array.from({ length: 24 }, (_, h) => <span key={h} className="text-[9px] text-slate-400 text-center">{h % 3 === 0 ? h : ""}</span>)}
                {grid.map((row, d) => (
                    <React.Fragment key={d}>
                        <span className="text-[10px] font-semibold text-slate-500 leading-[18px]">{WEEKDAYS[d]}</span>
                        {row.map((v, h) => (
                            <span key={h} title={`${WEEKDAYS[d]} ${String(h).padStart(2, "0")}:00 — ${v} page view${v === 1 ? "" : "s"}`}
                                className="h-[18px] rounded-[3px]" style={{ background: v ? `rgba(99,102,241,${0.15 + 0.85 * (v / max)})` : "#f1f5f9" }} />
                        ))}
                    </React.Fragment>
                ))}
            </div>
            <div className="flex items-center gap-1.5 mt-3 text-[10px] text-slate-400">
                Less {[0.15, 0.4, 0.65, 1].map(o => <span key={o} className="w-3 h-3 rounded-[3px]" style={{ background: `rgba(99,102,241,${o})` }} />)} More
            </div>
        </div>
    );
}

function Donut({ items, icons = {} }) {
    const total = items.reduce((a, x) => a + x.value, 0);
    if (!total) return <p className="py-10 text-center text-sm text-slate-400">No data</p>;
    return (
        <div className="flex items-center gap-6">
            <div className="w-36 h-36 shrink-0">
                <ResponsiveContainer width="100%" height="100%">
                    <PieChart><Pie data={items} dataKey="value" nameKey="name" innerRadius="62%" outerRadius="100%" paddingAngle={2} stroke="none">
                        {items.map((_, i) => <Cell key={i} fill={PALETTE[i % PALETTE.length]} />)}
                    </Pie><Tooltip contentStyle={{ borderRadius: 12, fontSize: 12 }} /></PieChart>
                </ResponsiveContainer>
            </div>
            <ul className="flex-1 space-y-2">
                {items.map((x, i) => {
                    const Icon = icons[x.name];
                    return (
                        <li key={x.name} className="flex items-center gap-2 text-sm">
                            <span className="w-2.5 h-2.5 rounded-full" style={{ background: PALETTE[i % PALETTE.length] }} />
                            {Icon && <Icon size={14} className="text-slate-400" />}
                            <span className="flex-1 capitalize text-slate-600">{x.name}</span>
                            <b className="text-navy-900">{Math.round((x.value / total) * 100)}%</b>
                        </li>
                    );
                })}
            </ul>
        </div>
    );
}

function Bars({ items }) {
    const total = items.reduce((a, x) => a + x.value, 0);
    if (!total) return <p className="py-8 text-center text-sm text-slate-400">No data</p>;
    return (
        <div style={{ height: Math.max(80, items.length * 30) }}>
            <ResponsiveContainer width="100%" height="100%">
                <BarChart data={items} layout="vertical" margin={{ top: 0, right: 36, left: 0, bottom: 0 }}>
                    <XAxis type="number" hide />
                    <YAxis type="category" dataKey="name" width={96} tick={{ fontSize: 12, fill: "#475569" }} tickLine={false} axisLine={false} />
                    <Tooltip cursor={{ fill: "#f8fafc" }} contentStyle={{ borderRadius: 12, fontSize: 12 }} formatter={v => [`${v} (${Math.round((v / total) * 100)}%)`, "Visitors"]} />
                    <Bar dataKey="value" radius={[0, 6, 6, 0]} barSize={14} label={{ position: "right", fontSize: 11, fill: "#64748b" }}>
                        {items.map((_, i) => <Cell key={i} fill={PALETTE[i % PALETTE.length]} />)}
                    </Bar>
                </BarChart>
            </ResponsiveContainer>
        </div>
    );
}
